<template>
  <div class="node-wrapper">
    <div
      class="cert-card card"
      :class="cardClasses"
      :data-cert-id="node.cert.id"
    >
      <div class="card-body p-2">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <span class="fw-bold">Cert #{{ node.cert.id }}</span>
            <span class="badge bg-secondary ms-1">
              {{ node.cert.count }} MWh
            </span>
          </div>
          <span
            v-if="node.cert.transferred_at"
            class="badge bg-warning text-dark"
          >
            transferred
          </span>
          <span v-else-if="node.cert.cancelled_at" class="badge bg-danger">
            cancelled
          </span>
          <span v-else class="badge bg-success">active</span>
        </div>
        <div class="small text-body-secondary mt-1">
          <div v-if="node.cert.bundle_start">
            bundle_start: {{ node.cert.bundle_start }}
          </div>
          <div v-if="node.cert.bundle_end">
            bundle_end: {{ node.cert.bundle_end }}
          </div>
          <div v-if="node.cert.production_period_start">
            {{ formatDateTimeIntoDate(node.cert.production_period_start) }} –
            {{ formatDateTimeIntoDate(node.cert.production_period_end) }}
          </div>
        </div>
      </div>

      <div v-if="items.length" class="card-footer p-0">
        <button
          class="btn btn-sm btn-link w-100 text-start text-decoration-none px-2 py-1"
          @click="piExpanded = !piExpanded"
        >
          <FontAwesomeIcon
            :icon="piExpanded ? 'chevron-down' : 'chevron-right'"
            class="me-1"
            size="xs"
          />
          {{ items.length }} portfolio item{{ items.length !== 1 ? "s" : "" }}
          <span class="text-body-secondary ms-1">
            · {{ distinctCompanyCount }}
            {{ distinctCompanyCount === 1 ? "company" : "companies" }}
          </span>
          <span class="text-body-secondary ms-1"> ({{ piSummary }}) </span>
        </button>
        <div v-if="piExpanded" class="pi-list px-2 pb-2">
          <div
            v-for="pi in items"
            :key="pi.id"
            class="pi-row small d-flex align-items-center gap-1 py-1"
            :class="{ 'opacity-50': pi.deleted_at }"
          >
            <span
              class="pi-dot"
              :style="{
                backgroundColor:
                  portfolioColorMap[pi.portfolio_type] ?? '#6c757d',
              }"
            ></span>
            <span class="fw-medium">PI #{{ pi.id }}</span>
            <span
              class="badge badge-sm"
              :style="piBadgeStyle(pi.portfolio_type)"
            >
              {{ pi.count }} MWh
            </span>
            <span class="text-body-secondary text-truncate">
              {{ pi.portfolio_name }}
            </span>
            <span
              class="text-body-secondary text-truncate"
              :title="pi.company_name"
            >
              [{{ pi.company_name }}]
            </span>
            <span v-if="pi.deleted_at" class="text-danger">[del]</span>
            <span
              v-if="pi.sales_transaction_item_id"
              class="badge bg-info text-dark badge-sm"
            >
              sold
            </span>
            <span v-if="pi.company_order_id" class="text-body-secondary">
              O#{{ pi.company_order_id }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="node.children.length" class="children-container">
      <CertificateChainNode
        v-for="child in node.children"
        :key="child.cert.id"
        :node="child"
        :portfolioItems="portfolioItems"
        :portfolioColorMap="portfolioColorMap"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { sumBy } from "lodash-es";
import { formatDateTimeIntoDate } from "@/composables/formatUtils";
import type { PortfolioItem, TreeNode } from "./certificateChainTypes";

const props = defineProps<{
  node: TreeNode;
  portfolioItems: Map<number, PortfolioItem[]>;
  portfolioColorMap: Record<string, string>;
}>();

const piExpanded = ref(false);

const items = computed(
  () => props.portfolioItems.get(props.node.cert.id) ?? []
);

const piSummary = computed(() => {
  const live = items.value.filter((pi) => !pi.deleted_at);
  const sold = live.filter((pi) => pi.sales_transaction_item_id);
  const soldPart = sold.length ? `, ${sumBy(sold, "count")} MWh sold` : "";
  return `${sumBy(live, "count")} MWh active${soldPart}`;
});

const distinctCompanyCount = computed(
  () => new Set(items.value.map((pi) => pi.company_id)).size
);

const cardClasses = computed(() => ({
  "cert-transferred":
    !!props.node.cert.transferred_at || !!props.node.cert.cancelled_at,
}));

const piBadgeStyle = (portfolioType: string) => ({
  backgroundColor: props.portfolioColorMap[portfolioType] ?? "#6c757d",
  color: "#fff",
  fontSize: "0.7rem",
  padding: "1px 5px",
});
</script>

<style scoped lang="scss">
.node-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.cert-card {
  width: 280px;
  border-radius: 3px;
  border-width: 2px;
  position: relative;
}

.cert-transferred {
  opacity: 0.65;
}

.pi-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.pi-row {
  border-top: 1px solid var(--bs-border-color-translucent);
}

.children-container {
  display: flex;
  gap: 24px;
  margin-top: 32px;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: -32px;
    left: 50%;
    width: 2px;
    height: 16px;
    background-color: #6c757d;
  }

  > .node-wrapper {
    position: relative;

    &::before {
      content: "";
      position: absolute;
      top: -16px;
      left: 50%;
      width: 2px;
      height: 16px;
      background-color: #6c757d;
    }
  }
}

.children-container:has(> :nth-child(2)) {
  > .node-wrapper {
    &::after {
      content: "";
      position: absolute;
      top: -16px;
      height: 2px;
      background-color: #6c757d;
    }

    &:first-child::after {
      left: 50%;
      right: -12px;
    }

    &:last-child::after {
      left: -12px;
      right: 50%;
    }

    &:not(:first-child):not(:last-child)::after {
      left: -12px;
      right: -12px;
    }
  }
}
</style>
